import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Close as CloseIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import { useFirebaseService } from '../contexts/FirebaseServiceContext';
import { format } from 'date-fns';
import { GridDot } from '../types/AmslerGridData';

interface TestResultDialogProps {
  open: boolean;
  onClose: () => void;
  testData: {
    timestamp: number;
    eyeTested: string;
    dots: Array<{ x: number; y: number; intensity?: number; distortionRadius?: number; } | GridDot>;
    abnormalities?: string[];
    notes?: string;
  } | null;
  onShare?: () => void;
  onDownload?: () => void;
  onPrint?: () => void;
}

const TestResultDialog: React.FC<TestResultDialogProps> = ({
  open,
  onClose,
  testData,
  onShare,
  onDownload,
  onPrint
}) => {
  const [image, setImage] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const { firebaseService } = useFirebaseService();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (!open || !testData) {
      setImage(null);
      return;
    }

    setLoading(true);
    setError(false);

    try {
      // Generate image from grid data
      const gridData = {
        id: 'temp-id',
        userId: 'temp-user',
        timestamp: testData.timestamp,
        eyeTested: testData.eyeTested,
        // Ensure dots have the required timestamp property
        dots: testData.dots.map(dot => {
          // Create a new dot object with required fields
          const newDot: any = {
            x: dot.x,
            y: dot.y,
            timestamp: 'timestamp' in dot ? dot.timestamp : testData.timestamp
          };
          
          // Add optional fields if they exist
          if ('intensity' in dot && dot.intensity !== undefined) {
            newDot.intensity = dot.intensity;
          }
          
          if ('distortionRadius' in dot && dot.distortionRadius !== undefined) {
            newDot.distortionRadius = dot.distortionRadius;
          }
          
          return newDot;
        }),
        abnormalities: testData.abnormalities || [],
        notes: testData.notes || ''
      };
      
      const generatedImage = firebaseService.generateGridImage(gridData);
      setImage(generatedImage);
      setLoading(false);
    } catch (err) {
      console.error('Error generating test image:', err);
      setError(true);
      setLoading(false);
    }
  }, [open, testData, firebaseService]);

  const formatDate = (timestamp: number) => {
    return format(new Date(timestamp), 'MMMM d, yyyy');
  };

  const formatTime = (timestamp: number) => {
    return format(new Date(timestamp), 'h:mm a');
  };

  const handleShare = () => {
    if (onShare) onShare();
  };

  const handleDownload = () => {
    if (onDownload) onDownload();
  };

  const handlePrint = () => {
    if (onPrint) onPrint();
  };

  if (!testData) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          maxHeight: '100%',
          height: '100%',
          m: fullScreen ? 0 : 2,
          mt: fullScreen ? 0 : 7,
          borderRadius: fullScreen ? 0 : 2,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'rgba(18, 18, 18, 0.95)',
          border: '1px solid #333333',
          boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.5)'
        }
      }}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.85)'
        }
      }}
    >
      <AppBar position="sticky" elevation={0} sx={{ 
        backgroundColor: 'rgba(0, 188, 212, 0.95)',
        color: '#000000',
        zIndex: 1100,
        flexShrink: 0,
        borderBottom: '1px solid #008ba3'
      }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onClose}
            aria-label="close"
            sx={{ mr: 2 }}
          >
            <CloseIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" component="div" sx={{ color: '#000000', fontWeight: 500 }}>
              {testData.eyeTested === 'left' ? 'Left' : 'Right'} Eye Test
            </Typography>
            <Typography variant="subtitle2" component="div" sx={{ color: '#000000', opacity: 0.7 }}>
              {formatDate(testData.timestamp)} {formatTime(testData.timestamp)}
            </Typography>
          </Box>
          <IconButton color="inherit" onClick={handlePrint} aria-label="print">
            <PrintIcon />
          </IconButton>
          <IconButton color="inherit" onClick={handleShare} aria-label="share">
            <ShareIcon />
          </IconButton>
          <IconButton color="inherit" onClick={handleDownload} aria-label="download">
            <DownloadIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <DialogContent sx={{ 
        p: 0, 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(18, 18, 18, 0.95)',
        backgroundImage: 'none',
        overflow: 'auto',
        flex: 1
      }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress sx={{ color: '#00bcd4' }} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>
            An error occurred while loading the test image.
          </Alert>
        ) : image ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: '100%',
              overflow: 'auto',
              p: 2
            }}
          >
            <img
              src={image}
              alt="Amsler Grid Test Result"
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                borderRadius: '4px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.5)'
              }}
            />
          </Box>
        ) : (
          <Box sx={{ p: 4 }}>
            <Typography sx={{ color: '#ffffff' }}>No image available</Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TestResultDialog; 